import * as THREE from 'three';
import { makeAutoObservable } from 'mobx';
import type { FloorplannerStore } from '../store/floorplannerStore';
import { WallType } from '../types/floorplannerTypes';

// Lazy accessor to avoid circular dependency with floorplannerStore
let _floorplannerStore: FloorplannerStore | null = null;
const getFloorplannerStore = (): FloorplannerStore => {
  if (!_floorplannerStore) {
    const globalStore = (globalThis as typeof globalThis & { __boniFloorplannerStore?: FloorplannerStore }).__boniFloorplannerStore;
    if (globalStore) {
      _floorplannerStore = globalStore;
    } else {
      throw new Error('FloorplannerStore not yet initialized - SharedJointStateManager called too early');
    }
  }
  return _floorplannerStore;
};

/**
 * Represents a persistent joint state that survives connection/disconnection cycles
 */
export interface SharedJointState {
  /** Stable ID based on geometric position */
  id: string;
  /** Geometric center point of the joint (rounded for stability) */
  geometricCenter: THREE.Vector2;
  /** Primary wall ID that persists across operations */
  primaryWallId: string;
  /** Set of wall IDs currently participating in this joint */
  participantWalls: Set<string>;
  /** Type of joint based on current participants */
  jointType: 'T' | 'X' | 'MULTI';
  /** Timestamp when joint was created */
  created: number;
  /** Timestamp when joint was last modified */
  lastModified: number;
  /** Flag indicating this joint is temporarily affected by drag operations */
  isDragTemporary: boolean;
  /** Original participant count before drag operation */
  preDragParticipantCount?: number;
}

/**
 * SharedJointStateManager provides persistent joint state management that survives
 * connection/disconnection cycles, ensuring consistent rendering regardless of
 * operation order.
 *
 * Key features:
 * - Geometric position-based joint identification
 * - Stable primary wall election that persists across operations
 * - Drag-safe state preservation during temporary disconnections
 * - Deterministic behavior independent of connection order
 */
export class SharedJointStateManager {
  /** Map of joint states keyed by stable geometric IDs */
  private jointStates = new Map<string, SharedJointState>();

  /** Precision for geometric position rounding (3 decimal places = 1mm precision) */
  private readonly GEOMETRIC_PRECISION = 3;

  /** Distance tolerance for considering points as same location */
  private readonly POSITION_TOLERANCE = 0.001;

  constructor() {
    makeAutoObservable(this);
  }

  /** Debounce timeout for logging operations */
  private logDebounceTimeout: NodeJS.Timeout | null = null;

  /** Track the last operation to avoid duplicate logs */
  private lastLoggedOperation: string | null = null;

  /**
   * Register or update a multi-wall joint state.
   * This is the main entry point for joint state management.
   *
   * @param wallIds Array of wall IDs participating in the joint
   * @param jointCenter Geometric center point of the joint
   * @param isDragOperation Whether this is called during a drag operation
   * @returns The joint state (existing or newly created)
   */
  registerMultiJoint(
    wallIds: string[],
    jointCenter: THREE.Vector2,
    isDragOperation: boolean = false
  ): SharedJointState | null {
    // Only handle multi-wall joints (3+ walls)
    if (wallIds.length < 3) {
      return null;
    }

    const geometricId = this.generateGeometricId(jointCenter);
    const existingJoint = this.jointStates.get(geometricId);

    // Check if this exact operation was just performed to avoid duplicates
    const operationKey = `register_${geometricId}_${wallIds.sort().join(',')}_${isDragOperation}`;
    if (this.lastLoggedOperation === operationKey) {
      // Return existing joint without logging
      return existingJoint || null;
    }
    this.lastLoggedOperation = operationKey;

    if (existingJoint) {
      return this.updateExistingJoint(existingJoint, wallIds, isDragOperation);
    } else {
      return this.createNewJoint(geometricId, wallIds, jointCenter, isDragOperation);
    }
  }

  /**
   * Get the persistent joint state for a given set of walls or position
   */
  getJointState(wallIds: string[]): SharedJointState | null;
  getJointState(position: THREE.Vector2): SharedJointState | null;
  getJointState(wallIdsOrPosition: string[] | THREE.Vector2): SharedJointState | null {
    if (Array.isArray(wallIdsOrPosition)) {
      // Find joint by participant walls
      for (const joint of this.jointStates.values()) {
        if (this.wallSetsMatch(joint.participantWalls, new Set(wallIdsOrPosition))) {
          return joint;
        }
      }
      return null;
    } else {
      // Find joint by position
      const geometricId = this.generateGeometricId(wallIdsOrPosition);
      return this.jointStates.get(geometricId) || null;
    }
  }

  /**
   * Get the primary wall ID for a joint at a given position or with given participants
   */
  getPrimaryWallId(wallIds: string[]): string | null;
  getPrimaryWallId(position: THREE.Vector2): string | null;
  getPrimaryWallId(wallIdsOrPosition: string[] | THREE.Vector2): string | null {
    const joint = this.getJointState(wallIdsOrPosition as any);
    return joint?.primaryWallId || null;
  }

  /**
   * Notify that a wall has been disconnected (potentially temporarily during drag)
   */
  notifyWallDisconnected(wallId: string, isDragOperation: boolean = false): void {
    // Find joints containing this wall
    const affectedJoints: SharedJointState[] = [];

    for (const joint of this.jointStates.values()) {
      if (joint.participantWalls.has(wallId)) {
        affectedJoints.push(joint);
      }
    }

    affectedJoints.forEach(joint => {
      if (isDragOperation) {
        // During drag, mark as temporary and preserve state
        if (!joint.isDragTemporary) {
          joint.preDragParticipantCount = joint.participantWalls.size;
          joint.isDragTemporary = true;
          
        }
        joint.participantWalls.delete(wallId);
      } else {
        // Permanent disconnection - remove wall and check if joint should be dissolved
        joint.participantWalls.delete(wallId);

        // If primary wall is being disconnected, elect new primary
        if (joint.primaryWallId === wallId && joint.participantWalls.size > 0) {
          joint.primaryWallId = this.electPrimaryWall(Array.from(joint.participantWalls));
          
        }

        // Dissolve joint if too few participants remain
        if (joint.participantWalls.size < 3) {
          this.dissolveJoint(joint.id, 'insufficient_participants');
        } else {
          joint.lastModified = Date.now();
          joint.jointType = this.determineJointType(joint.participantWalls.size);
        }
      }
    });
  }

  /**
   * Notify that a wall has been reconnected (typically after a drag operation)
   */
  notifyWallReconnected(wallId: string, newPosition: THREE.Vector2, isDragOperation: boolean = false): void {
    if (isDragOperation) {
      // During drag operations, look for drag-temporary joints at or near this position
      for (const joint of this.jointStates.values()) {
        if (joint.isDragTemporary && joint.geometricCenter.distanceTo(newPosition) < this.POSITION_TOLERANCE) {
          joint.participantWalls.add(wallId);
          joint.lastModified = Date.now();

          

          // If we're back to original participant count, restore normal state
          if (joint.preDragParticipantCount && joint.participantWalls.size >= joint.preDragParticipantCount) {
            joint.isDragTemporary = false;
            joint.preDragParticipantCount = undefined;
            
          }
          return;
        }
      }
    }

    // Normal reconnection - will be handled by registerMultiJoint when the joint is detected
  }

  /**
   * End drag operation and clean up temporary states
   */
  endDragOperation(): void {
    const affectedJoints: string[] = [];

    for (const [jointId, joint] of this.jointStates.entries()) {
      if (joint.isDragTemporary) {
        if (joint.participantWalls.size < 3) {
          // Dissolve joints that lost too many participants during drag
          affectedJoints.push(jointId);
        } else {
          // Restore normal state for joints that maintained sufficient participants
          joint.isDragTemporary = false;
          joint.preDragParticipantCount = undefined;
          joint.lastModified = Date.now();
          
        }
      }
    }

    // Dissolve joints that became invalid during drag
    affectedJoints.forEach(jointId => {
      this.dissolveJoint(jointId, 'drag_operation_ended');
    });
  }

  /**
   * Get all joint states (for debugging and monitoring)
   */
  getAllJointStates(): Map<string, SharedJointState> {
    return new Map(this.jointStates);
  }

  /**
   * Clear all joint states (for floorplan reset)
   */
  clearAll(): void {
    const jointCount = this.jointStates.size;

    // Only log if there were actual joints to clear
    if (jointCount > 0) {
      this.jointStates.clear();
      this.lastLoggedOperation = null; // Reset operation tracking
      
    } else {
      // Silent clear when no joints exist
      this.jointStates.clear();
      this.lastLoggedOperation = null;
    }
  }

  /**
   * Get statistics about current joint states
   */
  getStats(): {
    totalJoints: number;
    dragTemporaryJoints: number;
    jointTypes: Record<string, number>;
  } {
    const stats = {
      totalJoints: this.jointStates.size,
      dragTemporaryJoints: 0,
      jointTypes: { T: 0, X: 0, MULTI: 0 }
    };

    for (const joint of this.jointStates.values()) {
      if (joint.isDragTemporary) {
        stats.dragTemporaryJoints++;
      }
      stats.jointTypes[joint.jointType]++;
    }

    return stats;
  }

  /**
   * Log current joint summary for verification
   */
  logJointSummary(): void {
    const stats = this.getStats();
    const jointDetails = Array.from(this.jointStates.values()).map(joint => ({
      id: joint.id,
      type: joint.jointType,
      primaryWall: joint.primaryWallId,
      participants: Array.from(joint.participantWalls),
      isDragTemp: joint.isDragTemporary,
      center: { x: joint.geometricCenter.x.toFixed(3), y: joint.geometricCenter.y.toFixed(3) }
    }));

    
  }

  /**
   * Debounced version of logJointSummary to prevent excessive logging
   */
  private debouncedLogSummary(): void {
    if (this.logDebounceTimeout) {
      clearTimeout(this.logDebounceTimeout);
    }

    this.logDebounceTimeout = setTimeout(() => {
      this.logJointSummary();
      this.logDebounceTimeout = null;
    }, 100); // 100ms debounce
  }

  // === Private Methods ===

  /**
   * Generate a stable geometric ID based on position
   */
  private generateGeometricId(center: THREE.Vector2): string {
    const x = center.x.toFixed(this.GEOMETRIC_PRECISION);
    const y = center.y.toFixed(this.GEOMETRIC_PRECISION);
    return `joint_geo_${x}_${y}`;
  }

  /**
   * Create a new joint state
   */
  private createNewJoint(
    geometricId: string,
    wallIds: string[],
    center: THREE.Vector2,
    isDragOperation: boolean
  ): SharedJointState {
    const primaryWallId = this.electPrimaryWall(wallIds);
    const now = Date.now();

    const joint: SharedJointState = {
      id: geometricId,
      geometricCenter: center.clone(),
      primaryWallId,
      participantWalls: new Set(wallIds),
      jointType: this.determineJointType(wallIds.length),
      created: now,
      lastModified: now,
      isDragTemporary: isDragOperation
    };

    if (isDragOperation) {
      joint.preDragParticipantCount = wallIds.length;
    }

    this.jointStates.set(geometricId, joint);

    

    // Log summary after creating new joint (debounced)
    this.debouncedLogSummary();

    return joint;
  }

  /**
   * Update an existing joint state
   */
  private updateExistingJoint(
    joint: SharedJointState,
    wallIds: string[],
    isDragOperation: boolean
  ): SharedJointState {
    const newParticipants = new Set(wallIds);
    const oldParticipants = new Set(joint.participantWalls);

    // Check if participants changed
    if (!this.wallSetsMatch(newParticipants, oldParticipants)) {
      joint.participantWalls = newParticipants;
      joint.lastModified = Date.now();

      // Update joint type based on new participant count
      joint.jointType = this.determineJointType(wallIds.length);

      // If primary wall is no longer a participant, elect new primary
      if (!newParticipants.has(joint.primaryWallId)) {
        const oldPrimary = joint.primaryWallId;
        joint.primaryWallId = this.electPrimaryWall(wallIds);
        
      }

      
    }

    return joint;
  }

  /**
   * Elect primary wall based on consistent criteria (widest wall, then alphabetically)
   */
  private electPrimaryWall(wallIds: string[]): string {
    if (wallIds.length === 0) return '';

    let primaryWallId = wallIds[0];
    let maxWidth = 0;

    // Find wall with maximum width, use alphabetical order as tiebreaker
    for (const wallId of wallIds) {
      const wall = getFloorplannerStore().wallsMap.get(wallId);
      const wallWidth = wall?.wallWidth || getFloorplannerStore().wallWidth;

      if (wallWidth > maxWidth || (wallWidth === maxWidth && wallId < primaryWallId)) {
        maxWidth = wallWidth;
        primaryWallId = wallId;
      }
    }

    return primaryWallId;
  }

  /**
   * Determine joint type based on participant count
   */
  private determineJointType(participantCount: number): 'T' | 'X' | 'MULTI' {
    switch (participantCount) {
      case 3: return 'T';
      case 4: return 'X';
      default: return 'MULTI';
    }
  }

  /**
   * Check if two wall sets match
   */
  private wallSetsMatch(set1: Set<string>, set2: Set<string>): boolean {
    if (set1.size !== set2.size) return false;

    for (const item of set1) {
      if (!set2.has(item)) return false;
    }

    return true;
  }

  /**
   * Dissolve a joint and remove it from state
   */
  private dissolveJoint(jointId: string, reason: string): void {
    const joint = this.jointStates.get(jointId);
    if (joint) {
      

      this.jointStates.delete(jointId);
    }
  }
}

// Export singleton instance
export const sharedJointStateManager = new SharedJointStateManager();